import { capitalCase } from 'change-case';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
import { Box, Card, Stack, Link, Tooltip, Container, Typography, Checkbox } from '@mui/material';
import { useState, useEffect } from 'react';
import GoogleButton from 'react-google-button';
import localStorage from 'redux-persist/es/storage';
import { usePdfDocumentLinks } from '../../hooks/usePdfDocuments';
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import Page from '../../components/Page';
import Logo from '../../components/Logo';
import Image from '../../components/Image';
import { DialogAnimate } from '../../components/animate';
import PdfViewer from '../../components/PdfViewer';
import axios from '../../utils/axios';

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

export default function Login() {
  const { method, googleSignIn } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState('');
  const { privacyDoc, termsDoc } = usePdfDocumentLinks();
  const [latestTermVersion, setLatestTermVersion] = useState(localStorage.getItem('latestTermsVersion') || '');
  const [latestPrivacyVersion, setLatestPrivacyVersion] = useState(localStorage.getItem('latestPrivacyVersion') || '');
  const [checkboxes, setCheckboxes] = useState(false);

  const getLatestDocumentsVersions = async () => {
    try {
      const { data } = await axios.get('/user-termsPrivacyDocuments');
      let latestTerms = '0';
      let latestPrivacy = '0';
      let _checkboxes = {
        legal1: (await localStorage.getItem('userPrivacyAgreed')) === 'true' || false,
        legal2: (await localStorage.getItem('userTermsAgreed')) === 'true' || false,
      };
      for (let i = 0; i < data.length; i++) {
        if (data[i].type === 'terms' && parseInt(data[i].version, 10) > parseInt(latestTerms, 10)) {
          latestTerms = data[i].version;
        }
        if (data[i].type === 'privacy' && parseInt(data[i].version, 10) > parseInt(latestPrivacy, 10)) {
          latestPrivacy = data[i].version;
        }
      }
      if ((await localStorage.getItem('latestPrivacyVersion')) !== latestPrivacy.toString()) {
        _checkboxes = { ..._checkboxes, legal1: false };
        localStorage.setItem('userPrivacyAgreed', 'false');
      }
      if ((await localStorage.getItem('latestTermsVersion')) !== latestTerms.toString()) {
        _checkboxes = { ..._checkboxes, legal2: false };
        localStorage.setItem('userTermsAgreed', 'false');
      }
      setCheckboxes(_checkboxes);
      setLatestTermVersion(latestTerms);
      setLatestPrivacyVersion(latestPrivacy);
    } catch (e) {
      console.error('Error = ', e);
    }
  };

  useEffect(() => {
    const userBlocked = localStorage.getItem('userBlocked');
    if (userBlocked && userBlocked === '1') {
      enqueueSnackbar('The user or IP is blocked!', { variant: 'error' });
      localStorage.removeItem('userBlocked');
    }

    getLatestDocumentsVersions();
  }, []);

  const smUp = useResponsive('up', 'sm');

  const mdUp = useResponsive('up', 'md');

  return (
    <Page title="Login">
      <RootStyle>
        <HeaderStyle>
          <Logo />
          {/* {smUp && (
            <Typography variant="body2" sx={{ mt: { md: -2 } }}>
              Don’t have an account? {''}
              <Link variant="subtitle2" component={RouterLink} to={PATH_AUTH.register}>
                Get started
              </Link>
            </Typography>
          )} */}
        </HeaderStyle>

        {mdUp && (
          <SectionStyle>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              Hi, Welcome Back
            </Typography>
            <Image visibleByDefault disabledEffect src="/assets/illustrations/illustration_login.png" alt="login" />
          </SectionStyle>
        )}

        <Container maxWidth="sm">
          <ContentStyle>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" gutterBottom>
                  Sign in to Jobs
                </Typography>
                {/* <Typography sx={{ color: 'text.secondary' }}>Enter your details below.</Typography> */}
              </Box>


            </Stack>
            <Stack sx={{ mt: 2 }} spacing={2}>
              <Stack direction="row">
                {checkboxes && (
                  <Checkbox
                    defaultChecked={checkboxes.legal1}
                    value={checkboxes.legal1}
                    onChange={(event) => {
                      localStorage.setItem('userPrivacyAgreed', event.target.checked);
                      setCheckboxes({ ...checkboxes, legal1: event.target.checked });
                      if (event.target.checked) {
                        localStorage.setItem('latestPrivacyVersion', latestPrivacyVersion);
                      }
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ mt: '2%' }}>
                  I Agree To The {''}
                  <Link
                    variant="subtitle2"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedDoc(privacyDoc);
                      setOpenModal(true);
                    }}
                  >
                    Privacy Statement
                  </Link>
                </Typography>
              </Stack>
              <Stack sx={{ mb: 2 }} direction="row">
                {checkboxes && (
                  <Checkbox
                    defaultChecked={checkboxes.legal2}
                    value={checkboxes.legal2}
                    onChange={(event) => {
                      localStorage.setItem('userTermsAgreed', event.target.checked);
                      setCheckboxes({ ...checkboxes, legal2: event.target.checked });
                      if (event.target.checked) {
                        localStorage.setItem('latestTermsVersion', latestTermVersion);
                      }
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ mt: '2%' }}>
                  I Agree To The {''}
                  <Link
                    variant="subtitle2"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedDoc(termsDoc);
                      setOpenModal(true);
                    }}
                  >
                    Terms Of Service
                  </Link>
                </Typography>
              </Stack>
              <GoogleButton
                disabled={!(checkboxes.legal1 && checkboxes.legal2)}
                style={{ width: 'auto' }}
                onClick={() => {
                  googleSignIn();
                }}
              />
            </Stack>
            <DialogAnimate size="md" open={openModal} onClose={() => setOpenModal(false)}>
              <PdfViewer pdf={selectedDoc} height="90vh" />
            </DialogAnimate>
            {/* {!showEmailForm && <Button onClick={() => setShowEmailForm(true)} sx={{mt: 5}} variant="contained" size="large"> */}
            {/*  Sign in via Email */}
            {/* </Button>} */}

            {/* {showEmailForm && <><Alert severity="info" sx={{mb: 3}}> */}
            {/*  Use email : <strong>demo@minimals.cc</strong> / password :<strong> demo1234</strong> */}
            {/* </Alert> */}

            {/*  <LoginForm /> */}

            {/* {!smUp && ( */}
            {/*  <Typography variant="body2" align="center" sx={{mt: 3}}> */}
            {/*  Don’t have an account?{' '} */}
            {/*  <Link variant="subtitle2" component={RouterLink} to={PATH_AUTH.register}> */}
            {/*  Get started */}
            {/*  </Link> */}
            {/*  </Typography> */}
            {/*  )} */}
            {/*  <Button color="warning" onClick={() => setShowEmailForm(false)} sx={{mt: 2}} variant="outlined" size="lage"> */}
            {/*    Back */}
            {/*  </Button> */}
            {/* </>} */}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
